import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PodcastPlayer.css';

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const BAR_COUNT = 48;

function formatTime(sec) {
    if (!isFinite(sec) || sec < 0) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
}

function loadAutoplayPref() {
    try {
        return localStorage.getItem('podcast-autoplay-next') === '1';
    } catch (e) {
        return false;
    }
}

/**
 * PodcastPlayer — پخش‌کننده حرفه‌ای پادکست با موج صوتی، کنترل سرعت،
 * ذخیره‌ی خودکار موقعیت پخش و پخش خودکار قسمت بعد.
 *
 * Props:
 *  - src: آدرس فایل صوتی
 *  - title: عنوان اصلی (نام پادکست)
 *  - subtitle: زیرعنوان (عنوان قسمت)
 *  - storageKey: کلید یکتا برای ذخیره موقعیت پخش (مثلاً id قسمت)
 *  - onEnded: صدا زده می‌شود وقتی قسمت تمام شود (برای پخش خودکار بعدی)
 *  - hasNext: آیا قسمت بعدی برای Auto-play وجود دارد
 */
function PodcastPlayer({ src, title, subtitle, storageKey, onEnded, hasNext, onTime, onDuration, seekRequest }) {
    const audioRef = useRef(null);
    const canvasRef = useRef(null);
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const sourceRef = useRef(null);
    const rafRef = useRef(null);
    const resumedRef = useRef(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [volume, setVolume] = useState(1);
    const [muted, setMuted] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const [error, setError] = useState(false);
    const [autoplayNext, setAutoplayNext] = useState(loadAutoplayPref);

    // ===== راه‌اندازی Web Audio برای نمایش موج صوتی (best-effort؛ اگر CORS اجازه ندهد، بی‌صدا رد می‌شود) =====
    const ensureAudioGraph = useCallback(() => {
        if (sourceRef.current || !audioRef.current) return;
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextClass) return;
            const ctx = audioCtxRef.current || new AudioContextClass();
            audioCtxRef.current = ctx;
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 128;
            const source = ctx.createMediaElementSource(audioRef.current);
            source.connect(analyser);
            analyser.connect(ctx.destination);
            analyserRef.current = analyser;
            sourceRef.current = source;
        } catch (e) {
            // مرورگر یا سرور فایل صوتی اجازه تحلیل ندهد؛ فقط انیمیشن ساده جایگزین می‌شود
            sourceRef.current = null;
        }
    }, []);

    // ===== حلقه رسم موج صوتی =====
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        const ctx2d = canvas.getContext('2d');
        let phase = 0;

        const draw = () => {
            const { width, height } = canvas;
            ctx2d.clearRect(0, 0, width, height);
            const barWidth = width / BAR_COUNT;
            const analyser = analyserRef.current;

            let values;
            if (analyser && isPlaying) {
                const data = new Uint8Array(analyser.frequencyBinCount);
                analyser.getByteFrequencyData(data);
                const step = Math.max(1, Math.floor(data.length / BAR_COUNT));
                values = new Array(BAR_COUNT).fill(0).map((_, i) => data[i * step] / 255);
            } else {
                // انیمیشن جایگزین (idle/فالبک) وقتی تحلیل واقعی صوت در دسترس نیست
                phase += isPlaying ? 0.12 : 0.02;
                values = new Array(BAR_COUNT).fill(0).map((_, i) =>
                    isPlaying ? 0.15 + Math.abs(Math.sin(phase + i * 0.35)) * 0.55 : 0.06
                );
            }

            values.forEach((v, i) => {
                const barHeight = Math.max(2, v * height);
                const x = i * barWidth;
                ctx2d.fillStyle = 'var(--pp-bar-color, #6366f1)';
                ctx2d.fillRect(x, (height - barHeight) / 2, barWidth - 2, barHeight);
            });

            rafRef.current = requestAnimationFrame(draw);
        };

        draw();
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [isPlaying]);

    // ===== بازنشانی هنگام تغییر منبع صوتی =====
    useEffect(() => {
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        setError(false);
        resumedRef.current = null;
        // توجه: عنصر <audio> در DOM عوض نمی‌شود (فقط src آن تغییر می‌کند)،
        // پس نیازی به ساخت دوباره‌ی گراف Web Audio نیست و آن را دست‌نخورده نگه می‌داریم.
    }, [src]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.playbackRate = playbackRate;
    }, [playbackRate]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.volume = volume;
        audio.muted = muted;
    }, [volume, muted]);

    const handleLoadedMetadata = () => {
        const audio = audioRef.current;
        if (!audio) return;
        setDuration(audio.duration || 0); onDuration?.(audio.duration || 0);

        // ===== ادامه از آخرین موقعیت پخش =====
        if (storageKey && resumedRef.current !== storageKey) {
            resumedRef.current = storageKey;
            try {
                const saved = parseFloat(localStorage.getItem(`podcast-progress-${storageKey}`) || '0');
                if (saved > 5 && audio.duration && saved < audio.duration - 5) {
                    audio.currentTime = saved;
                    setCurrentTime(saved);
                }
            } catch (e) {
                // ignore
            }
        }
    };

    const handleTimeUpdate = () => {
        const audio = audioRef.current;
        if (!audio) return;
        setCurrentTime(audio.currentTime); onTime?.(audio.currentTime);
        if (storageKey) {
            try {
                localStorage.setItem(`podcast-progress-${storageKey}`, String(audio.currentTime));
            } catch (e) {
                // ignore
            }
        }
    };

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        ensureAudioGraph();
        if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
            audioCtxRef.current.resume().catch(() => {});
        }
        if (isPlaying) {
            audio.pause();
        } else {
            audio.play().catch(() => setError(true));
        }
    };

    const skip = (deltaSec) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;
        audio.currentTime = Math.max(0, Math.min(duration, audio.currentTime + deltaSec));
    };

    const handleSeek = (e) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;
        const value = parseFloat(e.target.value);
        audio.currentTime = value;
        setCurrentTime(value);
    };

    const handleEnded = () => {
        setIsPlaying(false);
        if (storageKey) {
            try {
                localStorage.removeItem(`podcast-progress-${storageKey}`);
            } catch (e) {
                // ignore
            }
        }
        if (autoplayNext && hasNext && onEnded) {
            onEnded();
        }
    };

    const toggleAutoplayNext = () => {
        setAutoplayNext((v) => {
            const next = !v;
            try {
                localStorage.setItem('podcast-autoplay-next', next ? '1' : '0');
            } catch (e) {
                // ignore
            }
            return next;
        });
    };

    useEffect(() => {
        if (!seekRequest || !audioRef.current || !Number.isFinite(seekRequest.time)) return;
        audioRef.current.currentTime = Math.max(0, Math.min(duration || Infinity, seekRequest.time));
        setCurrentTime(audioRef.current.currentTime);
    }, [seekRequest, duration]);

    const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <div className="podcast-player">
            <audio
                ref={audioRef}
                src={src}
                crossOrigin="anonymous"
                preload="metadata"
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onWaiting={() => setIsBuffering(true)}
                onPlaying={() => setIsBuffering(false)}
                onEnded={handleEnded}
                onError={() => setError(true)}
            />

            <div className="podcast-player-head">
                <div className="podcast-player-titles">
                    <div className="podcast-player-title">{title}</div>
                    {subtitle && <div className="podcast-player-subtitle">{subtitle}</div>}
                </div>
                <label className="podcast-autoplay-toggle" title="پخش خودکار قسمت بعد">
                    <input type="checkbox" checked={autoplayNext} onChange={toggleAutoplayNext} />
                    <span>پخش خودکار بعدی</span>
                </label>
            </div>

            <canvas ref={canvasRef} className="podcast-waveform" width={600} height={64} />

            {error && (
                <div className="podcast-player-error">⚠️ خطا در پخش فایل صوتی. لطفاً فایل را مستقیم دانلود کنید.</div>
            )}

            <div className="podcast-seek-row">
                <span className="podcast-time">{formatTime(currentTime)}</span>
                <input
                    type="range"
                    className="podcast-seek-bar"
                    min={0}
                    max={duration || 0}
                    step={0.1}
                    value={currentTime}
                    onChange={handleSeek}
                    style={{ '--progress': `${progressPct}%` }}
                />
                <span className="podcast-time">{formatTime(duration)}</span>
            </div>

            <div className="podcast-controls-row">
                <select
                    className="podcast-speed-select"
                    value={playbackRate}
                    onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
                    title="سرعت پخش"
                >
                    {SPEEDS.map((s) => (
                        <option key={s} value={s}>
                            {s}x
                        </option>
                    ))}
                </select>

                <div className="podcast-main-controls">
                    <button type="button" onClick={() => skip(-10)} title="۱۰ ثانیه عقب">
                        ⏪ 10
                    </button>
                    <button
                        type="button"
                        className="podcast-play-btn"
                        onClick={togglePlay}
                        title={isPlaying ? 'مکث' : 'پخش'}
                    >
                        {isBuffering ? '⏳' : isPlaying ? '⏸️' : '▶️'}
                    </button>
                    <button type="button" onClick={() => skip(10)} title="۱۰ ثانیه جلو">
                        10 ⏩
                    </button>
                </div>

                <div className="podcast-volume-group">
                    <button type="button" onClick={() => setMuted((m) => !m)} title={muted ? 'باز کردن صدا' : 'قطع صدا'}>
                        {muted || volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}
                    </button>
                    <input
                        type="range"
                        className="podcast-volume-bar"
                        min={0}
                        max={1}
                        step={0.01}
                        value={muted ? 0 : volume}
                        onChange={(e) => {
                            setVolume(parseFloat(e.target.value));
                            setMuted(false);
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

export default PodcastPlayer;
